import { useState, useEffect, Suspense } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesApi } from '../lib/sites';
import { usageApi } from '../lib/usage';
import { siteControllersApi, SiteController, CreateSiteControllerData, UpdateSiteControllerData } from '../lib/site-controllers';
import { groupsApi, GroupResponse } from '../lib/groups';
import { usersApi } from '../lib/users';
import { presetsApi, PresetResponse, CreatePresetData, UpdatePresetData } from '../lib/presets';
import { areasApi, AreaResponse, CreateAreaData, UpdateAreaData } from '../lib/areas';
import { useAuth } from '../hooks/useAuth';
import SiteControllerFormModal from '../components/site-controllers/SiteControllerFormModal';
import GroupsList from '../components/groups/GroupsList';
import PresetFormModal from '../components/presets/PresetFormModal';
import SetPresetDurationDialog from '../components/presets/SetPresetDurationDialog';
import AreaFormModal from '../components/areas/AreaFormModal';
import SiteControllersPanel from '../components/site-controllers/SiteControllersPanel';
import SiteOverview from '../components/sites/SiteOverview';
import SitesLocation from '../components/sites/SitesLocation';
import SiteAdministrators from '../components/sites/SiteAdministrators';
import { Plus, Trash2, LayoutDashboard, BarChart3, Server, Users, Shield, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import UsageSummaryTable from '@/components/usage/UsageSummaryTable';

enum SiteDetailTab {
  Overview = 'overview',
  Location = 'location',
  Usage = 'usage',
  Controllers = 'controllers',
  Groups = 'groups',
  Administrators = 'administrators',
}

export default function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isControllerModalOpen, setIsControllerModalOpen] = useState(false);
  const [editingController, setEditingController] = useState<SiteController | null>(null);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<PresetResponse | null>(null);
  const [selectedAreaForPreset, setSelectedAreaForPreset] = useState<string | null>(null);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<AreaResponse | null>(null);
  const [selectedControllerForArea, setSelectedControllerForArea] = useState<string | null>(null);
  const [isSetPresetModalOpen, setIsSetPresetModalOpen] = useState(false);
  const [deleteControllerDialog, setDeleteControllerDialog] = useState<{ open: boolean; controllerId: string | null }>({ open: false, controllerId: null });
  const [deleteGroupDialog, setDeleteGroupDialog] = useState<{ open: boolean; groupId: string | null }>({ open: false, groupId: null });
  const [deletePresetDialog, setDeletePresetDialog] = useState<{ open: boolean; presetId: string | null; areaId: string | null }>({ open: false, presetId: null, areaId: null });
  const [deleteAreaDialog, setDeleteAreaDialog] = useState<{ open: boolean; areaId: string | null; controllerId: string | null }>({ open: false, areaId: null, controllerId: null });
  const [deleteSiteDialogOpen, setDeleteSiteDialogOpen] = useState(false);
  const [uiAlert, setUiAlert] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: 'Notice',
    message: '',
  });
  const [pendingPresetSelection, setPendingPresetSelection] = useState<{ areaId: string; presetId: string } | null>(null);
  const [durationMinutes, setDurationMinutes] = useState<number | ''>(30);
  const [syncControllerDialog, setSyncControllerDialog] = useState<{ open: boolean; controllerId: string | null }>({ open: false, controllerId: null });

  // Get default tab from search params, fallback to 'overview'
  const tabFromParams = searchParams.get('tab');
  const isValidTab = tabFromParams && Object.values(SiteDetailTab).includes(tabFromParams as SiteDetailTab);
  const defaultTab = isValidTab ? (tabFromParams as SiteDetailTab) : SiteDetailTab.Overview;
  const [activeTab, setActiveTab] = useState<SiteDetailTab>(defaultTab);
  
  // Update active tab when search param changes
  useEffect(() => {
    if (isValidTab) {
      setActiveTab(tabFromParams as SiteDetailTab);
    } else if (tabFromParams) {
      // Invalid tab param, reset to default and remove from URL
      setActiveTab(SiteDetailTab.Overview);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tab');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [tabFromParams, isValidTab, searchParams, navigate]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    // Validate that the value is a valid tab enum value
    if (Object.values(SiteDetailTab).includes(value as SiteDetailTab)) {
      const tabValue = value as SiteDetailTab;
      setActiveTab(tabValue);
      const newSearchParams = new URLSearchParams(searchParams);
      if (tabValue === SiteDetailTab.Overview) {
        // Remove tab param if it's the default
        newSearchParams.delete('tab');
      } else {
        newSearchParams.set('tab', tabValue);
      }
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  };

  const canManage = currentUser?.role === 'super_user' || currentUser?.role === 'site_admin';
  const canConfigure = currentUser?.role === 'super_user';
  const isEndUser = currentUser?.role === 'end_user';

  const { data: site, isLoading: siteLoading } = useQuery({
    queryKey: ['site', id],
    queryFn: () => sitesApi.getById(id!),
    enabled: !!id,
  });

  const { data: controllers, isLoading: controllersLoading } = useQuery({
    queryKey: ['site-controllers', id],
    queryFn: () => siteControllersApi.getBySite(id!),
    enabled: !!id,
  });

  // Fetch area preset states for the chart
  const { data: areaPresetStates } = useQuery({
    queryKey: ['area-preset-states', id],
    queryFn: () => usageApi.getEvents({ siteId: id }),
    enabled: !!id,
  });

  // Fetch all areas for the site (needed for chart labels)
  const { data: allAreas } = useQuery({
    queryKey: ['all-areas', id],
    queryFn: async () => {
      if (!controllers) return [];
      const areaPromises = controllers.map(controller => 
        areasApi.getBySiteController(controller.id)
      );
      const areaArrays = await Promise.all(areaPromises);
      return areaArrays.flat();
    },
    enabled: !!id && !!controllers,
  });

  // Fetch areas grouped by controller for the controllers tab
  const { data: areasByController } = useQuery({
    queryKey: ['areas-by-controller', id, controllers?.map(c => c.id)],
    queryFn: async () => {
      if (!controllers) return new Map();
      const areaPromises = controllers.map(async (controller) => {
        const areas = await areasApi.getBySiteController(controller.id);
        return { controllerId: controller.id, areas };
      });
      const results = await Promise.all(areaPromises);
      const map = new Map<string, AreaResponse[]>();
      results.forEach(({ controllerId, areas }) => {
        map.set(controllerId, areas);
      });
      return map;
    },
    enabled: !!id && !!controllers && controllers.length > 0,
  });

  // Fetch all presets for the site (needed for chart labels)
  const { data: allPresets } = useQuery({
    queryKey: ['all-presets', id],
    queryFn: async () => {
      if (!allAreas) return [];
      const presetPromises = allAreas.map(area => 
        presetsApi.getByArea(area.id)
      );
      const presetArrays = await Promise.all(presetPromises);
      return presetArrays.flat();
    },
    enabled: !!id && !!allAreas && allAreas.length > 0,
  });

  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ['groups', id],
    queryFn: () => groupsApi.getBySite(id!),
    enabled: !!id && canManage,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    enabled: canManage,
  });

  const createControllerMutation = useMutation({
    mutationFn: (data: CreateSiteControllerData) => siteControllersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-controllers', id] });
      setIsControllerModalOpen(false);
      setEditingController(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create controller';
      setUiAlert({ open: true, title: 'Error', message: errorMessage });
    },
  });

  const showMutationError = (error: unknown, fallbackMessage: string) => {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    const errorMessage = err.response?.data?.error || err.message || fallbackMessage;
    setUiAlert({ open: true, title: 'Error', message: errorMessage });
  };

  const updateControllerMutation = useMutation({
    mutationFn: ({ id: controllerId, data }: { id: string; data: UpdateSiteControllerData }) =>
      siteControllersApi.update(controllerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-controllers', id] });
      setIsControllerModalOpen(false);
      setEditingController(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update controller';
      setUiAlert({ open: true, title: 'Error', message: errorMessage });
    },
  });

  const deleteControllerMutation = useMutation({
    mutationFn: (controllerId: string) => siteControllersApi.delete(controllerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-controllers', id] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to delete controller');
    },
  });


  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => groupsApi.delete(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups', id] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to delete group');
    },
  });

  const createPresetMutation = useMutation({
    mutationFn: (data: CreatePresetData) => presetsApi.create(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['presets', created.areaId] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to create preset');
    },
  });

  const updatePresetMutation = useMutation({
    mutationFn: ({ id: presetId, data }: { id: string; data: UpdatePresetData }) =>
      presetsApi.update(presetId, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['presets', updated.areaId] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to update preset');
    },
  });

  const deletePresetMutation = useMutation({
    mutationFn: ({ presetId, areaId }: { presetId: string; areaId: string }) => {
      return presetsApi.delete(presetId).then(() => ({ areaId }));
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['presets', result.areaId] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to delete preset');
    },
  });

  const createAreaMutation = useMutation({
    mutationFn: (data: CreateAreaData) => areasApi.create(data),
    onSuccess: () => {
      if (selectedControllerForArea) {
        queryClient.invalidateQueries({ queryKey: ['areas', selectedControllerForArea] });
      }
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['areas-by-controller', id] });
      }
      setIsAreaModalOpen(false);
      setEditingArea(null);
      setSelectedControllerForArea(null);
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to create area');
    },
  });

  const updateAreaMutation = useMutation({
    mutationFn: ({ id: areaId, data }: { id: string; data: UpdateAreaData }) =>
      areasApi.update(areaId, data),
    onSuccess: (_, variables) => {
      // Invalidate areas for the controller that contains this area
      if (selectedControllerForArea) {
        queryClient.invalidateQueries({ queryKey: ['areas', selectedControllerForArea] });
      }
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['areas-by-controller', id] });
      }
      // Also invalidate the specific area to refresh currentPresetId
      queryClient.invalidateQueries({ queryKey: ['area', variables.id] });
      // Only close modal if it was opened for editing (not for setting current preset)
      if (isAreaModalOpen) {
        setIsAreaModalOpen(false);
        setEditingArea(null);
        setSelectedControllerForArea(null);
      }
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to update area');
    },
  });

  const setCurrentPresetMutation = useMutation({
    mutationFn: ({ areaId, presetId, durationMinutes: duration }: { areaId: string; presetId: string; durationMinutes?: number }) =>
      areasApi.setCurrentPreset(areaId, presetId, duration),
    onSuccess: (updatedArea) => {
      // Invalidate areas for the controller that contains this area
      queryClient.invalidateQueries({ queryKey: ['areas', updatedArea.siteControllerId] });
      // Invalidate the specific area
      queryClient.invalidateQueries({ queryKey: ['area', updatedArea.id] });
      // Invalidate presets for this area to refresh any preset-related data
      queryClient.invalidateQueries({ queryKey: ['presets', updatedArea.id] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to set current preset');
    },
  });

  const deleteAreaMutation = useMutation({
    mutationFn: (areaId: string) => areasApi.delete(areaId),
    onSuccess: () => {
      if (selectedControllerForArea) {
        queryClient.invalidateQueries({ queryKey: ['areas', selectedControllerForArea] });
      }
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['areas-by-controller', id] });
      }
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to delete area');
    },
  });

  const deleteSiteMutation = useMutation({
    mutationFn: (siteId: string) => sitesApi.delete(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate('/sites');
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to delete site');
    },
  });

  const addSiteAdminMutation = useMutation({
    mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) =>
      sitesApi.assignUser(siteId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-users', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to add administrator');
    },
  });

  const removeSiteAdminMutation = useMutation({
    mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) =>
      sitesApi.removeUser(siteId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-users', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to remove administrator');
    },
  });

  const syncControllerMutation = useMutation({
    mutationFn: (controllerId: string) => siteControllersApi.sync(controllerId),
    onSuccess: () => {
      // Invalidate all related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['site-controllers', id] });
      queryClient.invalidateQueries({ queryKey: ['areas-by-controller', id] });
      queryClient.invalidateQueries({ queryKey: ['all-areas', id] });
      queryClient.invalidateQueries({ queryKey: ['all-presets', id] });
      queryClient.invalidateQueries({ queryKey: ['area-preset-states', id] });
      setSyncControllerDialog({ open: false, controllerId: null });
    },
    onError: (error: unknown) => {
      showMutationError(error, 'Failed to sync controller');
      setSyncControllerDialog({ open: false, controllerId: null });
    },
  });

  const handleCreateController = () => {
    setEditingController(null);
    setIsControllerModalOpen(true);
  };

  const handleEditController = (controller: SiteController) => {
    setEditingController(controller);
    setIsControllerModalOpen(true);
  };

  const handleControllerSubmit = (data: CreateSiteControllerData | UpdateSiteControllerData) => {
    if (editingController) {
      updateControllerMutation.mutate({ id: editingController.id, data: data as UpdateSiteControllerData });
    } else {
      createControllerMutation.mutate(data as CreateSiteControllerData);
    }
  };

  const handleCreateGroup = () => {
    navigate('/groups/new', { state: { siteId: id } });
  };

  const handleEditGroup = (group: GroupResponse) => {
    navigate(`/groups/${group.id}`);
  };

  const handleDeleteGroup = (groupId: string) => {
    setDeleteGroupDialog({ open: true, groupId });
  };


  const handleSetCurrentPreset = (areaId: string, presetId: string | null, presetAddress?: number | null) => {
    if (presetId === null) return;
    // If "All Off" preset (address 4), apply immediately with duration 0
    if (presetAddress === 4) {
      setCurrentPresetMutation.mutate({
        areaId,
        presetId,
        durationMinutes: 0,
      });
      return;
    }

    setPendingPresetSelection({ areaId, presetId });
    setIsSetPresetModalOpen(true);
  };

  const presetDurations = [15, 30, 60, 90, 120, 180];
  const handleDurationSelect = (value: number) => {
    setDurationMinutes(value);
  };
  const handleCustomDurationChange = (val: string) => {
    if (val === '') {
      setDurationMinutes('');
    } else {
      const parsed = parseInt(val, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        setDurationMinutes(parsed);
      }
    }
  };

  const handleConfirmSetPreset = () => {
    if (!pendingPresetSelection) return;
    const durationValue = durationMinutes === '' ? 0 : durationMinutes;
    setCurrentPresetMutation.mutate({
      areaId: pendingPresetSelection.areaId,
      presetId: pendingPresetSelection.presetId,
      durationMinutes: durationValue && durationValue > 0 ? durationValue : 0,
    });
    setIsSetPresetModalOpen(false);
  };

  const handlePresetSubmit = async (
    data: CreatePresetData | UpdatePresetData,
  ): Promise<PresetResponse> => {
    if (editingPreset) {
      return updatePresetMutation.mutateAsync({ id: editingPreset.id, data });
    }
    return createPresetMutation.mutateAsync(data as CreatePresetData);
  };

  const handleAreaSubmit = (data: CreateAreaData | UpdateAreaData) => {
    if (editingArea) {
      updateAreaMutation.mutate({ id: editingArea.id, data });
    } else {
      createAreaMutation.mutate(data as CreateAreaData);
    }
  };

  const handleCreateArea = (controllerId: string) => {
    setEditingArea(null);
    setSelectedControllerForArea(controllerId);
    setIsAreaModalOpen(true);
  };

  const handleEditArea = (area: AreaResponse) => {
    setEditingArea(area);
    setSelectedControllerForArea(area.siteControllerId);
    setIsAreaModalOpen(true);
  };

  const handleDeleteArea = (areaId: string, controllerId: string) => {
    setDeleteAreaDialog({ open: true, areaId, controllerId });
  };

  const handleCreatePreset = (areaId: string) => {
    setEditingPreset(null);
    setSelectedAreaForPreset(areaId);
    setIsPresetModalOpen(true);
  };

  const handleEditPreset = (preset: PresetResponse) => {
    setEditingPreset(preset);
    setSelectedAreaForPreset(preset.areaId);
    setIsPresetModalOpen(true);
  };

  const handleDeletePreset = (presetId: string, areaId: string) => {
    setDeletePresetDialog({ open: true, presetId, areaId });
  };

  const handleUpdateControllerIp = (controllerId: string, ipAddress: string) => {
    updateControllerMutation.mutate({
      id: controllerId,
      data: { ipAddress: ipAddress || undefined },
    });
  };

  const handleSyncController = (controllerId: string) => {
    setSyncControllerDialog({ open: true, controllerId });
  };

  const handleDeleteController = (controllerId: string) => {
    setDeleteControllerDialog({ open: true, controllerId });
  };

  if (siteLoading || controllersLoading || groupsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!site) {
    return <div className="p-6">Site not found</div>;
  }

  return (
    <div className="px-2 py-3 sm:px-0">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-heading">{site.name}</h1>
            {site.address && <p className="text-muted-foreground mt-2 text-sm sm:text-base">{site.address}</p>}
            {site.description && <p className="text-muted-foreground mt-2 text-sm sm:text-base">{site.description}</p>}
          </div>
          {canConfigure && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteSiteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="mb-6">
          <TabsList className="flex w-full gap-1 overflow-x-auto">
            <TabsTrigger value={SiteDetailTab.Overview} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden md:inline text-xs md:text-sm">Overview</span>
            </TabsTrigger>
            <TabsTrigger value={SiteDetailTab.Location} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
              <MapPin className="h-4 w-4" />
              <span className="hidden md:inline text-xs md:text-sm">Location</span>
            </TabsTrigger>
            {canConfigure && (
              <TabsTrigger value={SiteDetailTab.Administrators} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
                <Shield className="h-4 w-4" />
                <span className="hidden md:inline text-xs md:text-sm">Administrators</span>
              </TabsTrigger>
            )}
            <TabsTrigger value={SiteDetailTab.Usage} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden md:inline text-xs md:text-sm">Usage</span>
            </TabsTrigger>
            {!isEndUser && (
              <TabsTrigger value={SiteDetailTab.Controllers} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
                <Server className="h-4 w-4" />
                <span className="hidden md:inline text-xs md:text-sm">Controllers</span>
              </TabsTrigger>
            )}
            {canManage && !isEndUser && (
              <TabsTrigger value={SiteDetailTab.Groups} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 text-foreground data-[state=inactive]:text-foreground/80">
                <Users className="h-4 w-4" />
                <span className="hidden md:inline text-xs md:text-sm">Groups</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value={SiteDetailTab.Overview}>
          {site && (
            <SiteOverview
              site={site}
              canEdit={canManage}
              onError={(message) =>
                setUiAlert({ open: true, title: 'Error', message })
              }
            />
          )}
        </TabsContent>

        <TabsContent value={SiteDetailTab.Location}>
          {site && (
            <SitesLocation
              site={site}
              canEdit={canManage}
              onError={(message) =>
                setUiAlert({ open: true, title: 'Error', message })
              }
            />
          )}
        </TabsContent>

        {canConfigure && id && (
          <TabsContent value={SiteDetailTab.Administrators}>
            <SiteAdministrators
              siteId={id}
              onAdd={(userId) => addSiteAdminMutation.mutate({ siteId: id, userId })}
              onRemove={(userId) => removeSiteAdminMutation.mutate({ siteId: id, userId })}
              isAdding={addSiteAdminMutation.isPending}
              isRemoving={removeSiteAdminMutation.isPending}
            />
          </TabsContent>
        )}

        <TabsContent value={SiteDetailTab.Usage}>
          <Card>
            <CardHeader>
              <CardTitle>Usage Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {areaPresetStates && allAreas && allPresets && users ? (
                <Suspense fallback={<div className="h-[400px] flex items-center justify-center">Loading chart...</div>}>
                  <UsageSummaryTable sites={[site]} />
                </Suspense>
              ) : (
                <div className="h-[400px] flex items-center justify-center">
                  <p className="text-muted-foreground">Loading usage data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {!isEndUser && (
          <TabsContent value={SiteDetailTab.Controllers}>
            <SiteControllersPanel
              controllers={controllers || []}
              areasByController={areasByController}
              siteCurfew={
                site
                  ? { start: site.curfewStart, end: site.curfewEnd }
                  : undefined
              }
              canConfigure={canConfigure}
              canManage={canManage}
              onCreateController={handleCreateController}
              onEditController={handleEditController}
              onUpdateController={handleUpdateControllerIp}
              onDeleteController={handleDeleteController}
              onSyncController={handleSyncController}
              onCreateArea={handleCreateArea}
              onEditArea={handleEditArea}
              onDeleteArea={handleDeleteArea}
              onCreatePreset={handleCreatePreset}
              onEditPreset={handleEditPreset}
              onDeletePreset={canConfigure ? handleDeletePreset : undefined}
              onSetCurrentPreset={canManage ? handleSetCurrentPreset : undefined}
            />
          </TabsContent>
        )}

        {canManage && !isEndUser && (
          <TabsContent value={SiteDetailTab.Groups}>
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>User Groups</CardTitle>
                  <Button
                    onClick={handleCreateGroup}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Group
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {groups && groups.length > 0 ? (
                  <GroupsList
                    groups={groups}
                    onEdit={handleEditGroup}
                    onDelete={handleDeleteGroup}
                    isDeleting={deleteGroupMutation.isPending}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No groups for this site</p>
                    <Button
                      onClick={handleCreateGroup}
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Group
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {isControllerModalOpen && id && (
        <SiteControllerFormModal
          controller={editingController}
          siteId={id}
          onSubmit={handleControllerSubmit}
          onClose={() => {
            setIsControllerModalOpen(false);
            setEditingController(null);
          }}
          isSubmitting={createControllerMutation.isPending || updateControllerMutation.isPending}
        />
      )}


      {isSetPresetModalOpen && pendingPresetSelection && (
      <SetPresetDurationDialog
        open={isSetPresetModalOpen}
        onOpenChange={setIsSetPresetModalOpen}
        durationMinutes={durationMinutes}
        presetDurations={presetDurations}
        onDurationSelect={handleDurationSelect}
        onCustomDurationChange={handleCustomDurationChange}
        onConfirm={handleConfirmSetPreset}
        isSubmitting={setCurrentPresetMutation.isPending}
      />
      )}

      {isPresetModalOpen && selectedAreaForPreset && groups && (
        <PresetFormModal
          preset={editingPreset}
          areaId={selectedAreaForPreset}
          siteId={id}
          groups={groups || []}
          onSubmit={handlePresetSubmit}
          onClose={() => {
            setIsPresetModalOpen(false);
            setEditingPreset(null);
            setSelectedAreaForPreset(null);
          }}
          isSubmitting={createPresetMutation.isPending || updatePresetMutation.isPending}
        />
      )}

      {isAreaModalOpen && selectedControllerForArea && (
        <AreaFormModal
          area={editingArea}
          siteControllerId={selectedControllerForArea}
          presets={[]} // Will be loaded by the modal if needed
          onSubmit={handleAreaSubmit}
          onClose={() => {
            setIsAreaModalOpen(false);
            setEditingArea(null);
            setSelectedControllerForArea(null);
          }}
          isSubmitting={createAreaMutation.isPending || updateAreaMutation.isPending}
        />
      )}

      {/* Delete Confirmation Dialogs */}
      <AlertDialog open={deleteControllerDialog.open} onOpenChange={(open) => setDeleteControllerDialog({ open, controllerId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the site controller.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteControllerDialog.controllerId) {
                  deleteControllerMutation.mutate(deleteControllerDialog.controllerId);
                }
                setDeleteControllerDialog({ open: false, controllerId: null });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteGroupDialog.open} onOpenChange={(open) => setDeleteGroupDialog({ open, groupId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteGroupDialog.groupId) {
                  deleteGroupMutation.mutate(deleteGroupDialog.groupId);
                }
                setDeleteGroupDialog({ open: false, groupId: null });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletePresetDialog.open} onOpenChange={(open) => setDeletePresetDialog({ open, presetId: null, areaId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the preset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletePresetDialog.presetId && deletePresetDialog.areaId) {
                  deletePresetMutation.mutate({ presetId: deletePresetDialog.presetId, areaId: deletePresetDialog.areaId });
                }
                setDeletePresetDialog({ open: false, presetId: null, areaId: null });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteAreaDialog.open} onOpenChange={(open) => setDeleteAreaDialog({ open, areaId: null, controllerId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the area.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteAreaDialog.areaId && deleteAreaDialog.controllerId) {
                  setSelectedControllerForArea(deleteAreaDialog.controllerId);
                  deleteAreaMutation.mutate(deleteAreaDialog.areaId);
                }
                setDeleteAreaDialog({ open: false, areaId: null, controllerId: null });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteSiteDialogOpen} onOpenChange={setDeleteSiteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this site?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the site and all associated controllers, areas, and presets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (id) {
                  deleteSiteMutation.mutate(id);
                }
                setDeleteSiteDialogOpen(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={syncControllerDialog.open} onOpenChange={(open) => setSyncControllerDialog({ open, controllerId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sync Controller with PDEG?</AlertDialogTitle>
            <AlertDialogDescription>
              This will synchronize the controller with the PDEG project.xml configuration. 
              Areas, Presets, and Lights may be created, updated, or deleted if they are no longer defined on the Site Controller PDEG.
              <br /><br />
              This action will update the database to match the current PDEG configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={syncControllerMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (syncControllerDialog.controllerId) {
                  syncControllerMutation.mutate(syncControllerDialog.controllerId);
                }
              }}
              disabled={syncControllerMutation.isPending}
            >
              {syncControllerMutation.isPending ? 'Syncing...' : 'Sync Controller'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={uiAlert.open} onOpenChange={(open) => setUiAlert((p) => ({ ...p, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
            <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

